import { useCallback, useEffect, useState } from "react";
import { portalApi } from "../api";
import { statusOf } from "../errors";
import { dateOnly, dateTime, remaining } from "../format";
import { useI18n } from "../i18n";
import { usePortal } from "../state";
import type { ListItem, MyTitle, Rental } from "../types";
import { EmptyState, Link, Loading, Notice, PageTitle, Poster } from "../ui";
import { FilmIcon, PlayIcon } from "../icons";
import { InformationRail } from "../information";

/**
 * *Mitt innehåll* (`GET /api/interactiveplayer/v1/portal/me/titles`).
 *
 * The countdown is computed from the SERVER's clock: the response carries
 * `serverTime`, and the time left is `endsAt - (serverTime + time elapsed
 * here since the answer arrived)`. A phone whose clock is an hour off still
 * shows the right number. The states come from the server's rental clock
 * (`@shared/consumer-tvod` rental.ts); this page never derives expiry on its
 * own beyond ticking an `active` countdown down.
 *
 * *Min lista* (`GET /api/interactiveplayer/v1/portal/me/list`, slice J) sits below the rentals:
 * what the member saved on a title page. It loads on its own, so a failure
 * there never hides the rentals.
 */

/** Ticks every 30 s so a countdown moves without a re-fetch. */
function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** The server's "now", advanced by the time elapsed on this device. */
export function serverNow(
  serverTime: string,
  receivedAt: number,
  now: number
): number {
  const base = new Date(serverTime).getTime();
  return base + Math.max(0, now - receivedAt);
}

export function RentalStatus(props: {
  rental: Rental;
  serverTime: string;
  receivedAt: number;
}) {
  const { t, lang } = useI18n();
  const now = useNow();
  const { rental } = props;
  const at = serverNow(props.serverTime, props.receivedAt, now);

  if (rental.state === "active") {
    if (!rental.endsAt) {
      return <p className="rental rental--active">{t("my.noLimit")}</p>;
    }
    const left = new Date(rental.endsAt).getTime() - at;
    if (left <= 0) {
      return (
        <p className="rental rental--expired">
          {t("my.endedAt", { date: dateTime(rental.endsAt, lang) })}
        </p>
      );
    }
    return (
      <p className="rental rental--active">
        <strong>{t("my.left", { time: remaining(left, t) })}</strong>
        <span className="muted"> · {dateTime(rental.endsAt, lang)}</span>
      </p>
    );
  }
  if (rental.state === "not_started") {
    return (
      <div className="rental">
        {rental.startBy && (
          <p>
            <strong>
              {t("my.startBy", { date: dateOnly(rental.startBy, lang) })}
            </strong>
          </p>
        )}
        {rental.firstPlayStartsClock && (
          <p className="muted">{t("my.clockOnPlay")}</p>
        )}
      </div>
    );
  }
  const ended = rental.endsAt ?? rental.startBy;
  return (
    <p className="rental rental--expired">
      {ended
        ? t("my.endedAt", { date: dateTime(ended, lang) })
        : t("my.expired")}
    </p>
  );
}

/** The state as a chip: word AND colour, never colour alone. */
function StatusChip({ state }: { state: Rental["state"] }) {
  const { t } = useI18n();
  const key =
    state === "active"
      ? "my.status.active"
      : state === "not_started"
        ? "my.status.notStarted"
        : "my.status.expired";
  const cls =
    state === "active"
      ? "status-chip status-chip--active"
      : state === "not_started"
        ? "status-chip status-chip--waiting"
        : "status-chip status-chip--ended";
  return <span className={cls}>{t(key)}</span>;
}

function TitleRow(props: {
  item: MyTitle;
  serverTime: string;
  receivedAt: number;
}) {
  const { item } = props;
  const { t } = useI18n();
  const state = item.rental?.state;
  return (
    <li className={`my-row${state === "expired" ? " my-row--ended" : ""}`}>
      <div className="my-row__media">
        <Poster
          src={item.images.landscape ?? item.images.poster}
          title={item.title}
          shape="landscape"
        />
      </div>
      <div className="my-row__body">
        {state && <StatusChip state={state} />}
        <h3 className="my-row__title">
          <Link to={`/titles/${encodeURIComponent(item.assetId)}`}>
            {item.title || item.assetId}
          </Link>
        </h3>
        {item.rental && (
          <RentalStatus
            rental={item.rental}
            serverTime={props.serverTime}
            receivedAt={props.receivedAt}
          />
        )}
        {(state === "active" || state === "not_started") && (
          <Link
            className="btn btn--primary btn--inline my-row__play"
            to={
              item.type === "series"
                ? `/titles/${encodeURIComponent(item.assetId)}`
                : `/watch/${encodeURIComponent(item.assetId)}`
            }
          >
            <PlayIcon className="btn__icon" />
            {t("title.play")}
          </Link>
        )}
      </div>
    </li>
  );
}

export function MyContentScreen() {
  const { t, lang } = useI18n();
  const { me, setMe } = usePortal();
  const [items, setItems] = useState<MyTitle[] | null>(null);
  const [serverTime, setServerTime] = useState("");
  const [receivedAt, setReceivedAt] = useState(0);
  const [saved, setSaved] = useState<ListItem[] | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const mine = await portalApi.myTitles(lang);
      setReceivedAt(Date.now());
      setServerTime(mine.serverTime);
      setItems(mine.items);
    } catch (err) {
      if (statusOf(err) === 401) setMe(null);
      else setFailed(true);
    }
  }, [lang, setMe]);

  useEffect(() => {
    if (me) void load();
  }, [me, load]);

  useEffect(() => {
    if (!me) return;
    let live = true;
    Promise.resolve()
      .then(() => portalApi.myList(lang))
      .then((r) => live && setSaved(r.items))
      .catch(() => live && setSaved(null));
    return () => {
      live = false;
    };
  }, [me, lang]);

  if (!me) {
    return (
      <section className="panel">
        <PageTitle>{t("my.title")}</PageTitle>
        <p>{t("my.loginFirst")}</p>
        <Link className="btn" to="/login?next=%2Fmy">
          {t("nav.login")}
        </Link>
      </section>
    );
  }
  if (failed) {
    return (
      <section className="panel">
        <Notice kind="error">{t("common.error")}</Notice>
        <button type="button" className="btn" onClick={() => void load()}>
          {t("common.retry")}
        </button>
      </section>
    );
  }
  if (!items) return <Loading />;

  const groups: Array<{
    key: "active" | "not_started" | "expired";
    label: string;
  }> = [
    { key: "active", label: t("my.active") },
    { key: "not_started", label: t("my.notStarted") },
    { key: "expired", label: t("my.expired") },
  ];

  return (
    <>
      <PageTitle>{t("my.title")}</PageTitle>
      {items.length === 0 && (
        <EmptyState
          icon={<FilmIcon />}
          title={t("my.emptyTitle")}
          action={
            <Link className="btn btn--primary btn--inline" to="/">
              {t("my.discover")}
            </Link>
          }
        >
          <p>{t("my.empty")}</p>
        </EmptyState>
      )}
      {groups.map((g) => {
        const list = items.filter((i) => i.rental?.state === g.key);
        if (list.length === 0) return null;
        return (
          <section key={g.key} aria-labelledby={`my-${g.key}`}>
            <h2 id={`my-${g.key}`} className="section-title">
              {g.label}
            </h2>
            <ul className="my-list">
              {list.map((item) => (
                <TitleRow
                  key={item.assetId}
                  item={item}
                  serverTime={serverTime}
                  receivedAt={receivedAt}
                />
              ))}
            </ul>
          </section>
        );
      })}

      {saved && (
        <section aria-labelledby="my-list-h" data-testid="my-list">
          <h2 id="my-list-h" className="section-title">
            {t("my.list")}
          </h2>
          {saved.length === 0 ? (
            <p className="muted">{t("my.listEmpty")}</p>
          ) : (
            <ul className="my-saved">
              {saved.map((item) => (
                <li key={item.assetId} className="my-saved__item">
                  <Link to={`/titles/${encodeURIComponent(item.assetId)}`}>
                    <Poster src={item.images.poster} title={item.title} />
                    <span className="my-saved__title">
                      {item.title || item.assetId}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      <InformationRail />
    </>
  );
}
